import { put, takeLatest, call } from 'redux-saga/effects';
import axios from 'axios';
import {
  REGISTER,
  LOAD_USER,
  LOGIN_USER,
  LOGIN_WITH_CODE,
  REQUEST_RESET_EMAIL,
  RESET_PASSWORD,
  GET_FREE_TRIAL_STATUS,
  REDEEM_FREE_TRIAL,
} from './constants';
import {
  registerSuccess,
  registerError,
  loadUserSuccess,
  loadUserError,
  loginUserSuccess,
  loginUserError,
  requestResetEmailSuccess,
  requestResetEmailError,
  resetPasswordSuccess,
  resetPasswordError,
  getFreeTrialStatusSuccess,
  getFreeTrialStatusError,
  redeemFreeTrialSuccess,
  redeemFreeTrialError,
} from './actions';
import setAuthToken from '../../helpers/utils';
import { addToMailChimp } from '../../helpers/utils';
import { normalizeError } from '../../helpers/utils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function* loadUser() {
  if (localStorage.token) {
    setAuthToken(localStorage.token);
  }

  try {
    const payload = yield call(axios.get, `${API_URL}/auth`);
    // A token 3+ days old comes back renewed (routes/api/auth.js), so people
    // who keep using the site stay signed in.
    const renewed = payload.headers?.['x-auth-token-renewed'];
    if (renewed) {
      localStorage.setItem('token', renewed);
      setAuthToken(renewed);
    }
    yield put(loadUserSuccess(payload.data));
  } catch (err) {
    console.log(err);
    localStorage.removeItem('token');
    const errors = normalizeError(err);
    yield put(loadUserError(errors));
  }
}

export function* register({ name, lastName, email, password, callback }) {
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const body = JSON.stringify({
    name,
    lastName,
    email,
    password,
  });

  const isInvalidEmailError = (error) => {
    return (
      error.response &&
      error.response.status === 400 &&
      error.response.data &&
      typeof error.response.data.error === 'string' &&
      error.response.data.error.includes('looks fake or invalid')
    );
  };

  try {
    // Validate email with Mailchimp first
    try {
      // 'Meal Plan Genie' is the legacy tag (kept so any Mailchimp
      // automation keyed on it still fires); 'App Signup' is the clear one.
      yield call(addToMailChimp, { name, lastName, email }, [
        'Meal Plan Genie',
        'App Signup',
      ]);
    } catch (mailchimpError) {
      if (isInvalidEmailError(mailchimpError)) {
        console.error(
          'Mailchimp rejected email:',
          mailchimpError.response?.data?.error || 'Unknown error'
        );

        yield put(
          registerError(normalizeError(mailchimpError.response.data.error))
        );
        return; // Stop registration process
      }
      console.error('Non-blocking Mailchimp error:', mailchimpError.message);
    }

    // Proceed with user registration
    const payload = yield call(axios.post, `${API_URL}/users`, body, config);

    // Save token and user ID in localStorage
    localStorage.setItem('token', payload.data.token);
    localStorage.setItem('userId', payload.data.user.id);

    // Set the Authorization header globally
    setAuthToken(payload.data.token);

    // Dispatch success action
    yield put(registerSuccess(payload.data));

    // Load user data after successful registration
    yield call(loadUser);

    // Execute callback if provided
    if (callback) callback();
  } catch (err) {
    const errors = normalizeError(err);
    console.log(errors);
    yield put(registerError(errors));
  }
}

export function* loginUser({ email, password, callback }) {
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const body = JSON.stringify({ email, password });
  try {
    const payload = yield call(axios.post, `${API_URL}/auth`, body, config);

    const token = payload.data.token;
    const user = payload.data.user;

    localStorage.setItem('token', token);
    localStorage.setItem('userId', user.id);

    yield put(loginUserSuccess(payload.data));
    yield call(loadUser);

    if (callback) callback(); // <-- Run the callback on success
  } catch (err) {
    const errors = normalizeError(err);
    console.log(errors);
    yield put(loginUserError(errors));
  }
}

// Same as loginUser, with the emailed code instead of a password.
export function* loginWithCode({ email, code, callback }) {
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const body = JSON.stringify({ email, code });
  try {
    const payload = yield call(
      axios.post,
      `${API_URL}/auth/login-code/verify`,
      body,
      config
    );

    localStorage.setItem('token', payload.data.token);
    localStorage.setItem('userId', payload.data.user.id);

    yield put(loginUserSuccess(payload.data));
    yield call(loadUser);

    if (callback) callback();
  } catch (err) {
    const errors = normalizeError(err);
    console.log(errors);
    yield put(loginUserError(errors));
  }
}

export function* requestResetEmail(email) {
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const body = JSON.stringify(email);

  try {
    const payload = yield call(
      axios.post,
      `${API_URL}/auth/request-reset`,
      body,
      config
    );

    const token = payload.data.token;
    localStorage.setItem('token', token);

    yield put(requestResetEmailSuccess(payload.data));
  } catch (err) {
    const errors = normalizeError(err);
    console.log(errors);
    yield put(requestResetEmailError(errors));
  }
}

export function* resetPassword({ email, token, newPassword }) {
  const config = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const body = JSON.stringify({ email, token, newPassword });

  try {
    const payload = yield call(
      axios.post,
      `${API_URL}/auth/reset-password`,
      body,
      config
    );

    const token = payload.data.token;
    localStorage.setItem('token', token);

    yield put(resetPasswordSuccess(payload.data));
  } catch (err) {
    const errors = normalizeError(err);
    console.log(errors);
    yield put(resetPasswordError(errors));
  }
}

// Free Trial Sagas
export function* getFreeTrialStatus() {
  try {
    const payload = yield call(axios.get, `${API_URL}/users/free-trial-status`);
    yield put(getFreeTrialStatusSuccess(payload.data));
  } catch (err) {
    const errors = normalizeError(err);
    console.log(errors);
    yield put(getFreeTrialStatusError(errors));
  }
}

export function* redeemFreeTrialSaga({ callback }) {
  try {
    const payload = yield call(axios.post, `${API_URL}/users/use-free-trial`);
    yield put(redeemFreeTrialSuccess(payload.data));

    // Execute callback if provided
    if (callback) callback();
  } catch (err) {
    const errors = normalizeError(err);
    console.log(errors);
    yield put(redeemFreeTrialError(errors));
  }
}

export default function* authSaga() {
  yield takeLatest(REGISTER, register);
  yield takeLatest(LOAD_USER, loadUser);
  yield takeLatest(LOGIN_USER, loginUser);
  yield takeLatest(LOGIN_WITH_CODE, loginWithCode);
  yield takeLatest(REQUEST_RESET_EMAIL, requestResetEmail);
  yield takeLatest(RESET_PASSWORD, resetPassword);
  yield takeLatest(GET_FREE_TRIAL_STATUS, getFreeTrialStatus);
  yield takeLatest(REDEEM_FREE_TRIAL, redeemFreeTrialSaga);
}
